import React, { useState, useCallback } from "react";
import { FaFileExcel, FaFilter, FaSearch, FaSync } from "react-icons/fa";

export default function AdminAuditReport({ allTransactions }) {
  // 1. Date and Search States
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  // 2. State to hold the "Locked" results for the table
  const [filteredResults, setFilteredResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // 3. Dedicated Search Function
  const executeSearch = useCallback(() => {
    setIsSearching(true);

    // Small timeout to simulate a "refresh" feel for the user
    setTimeout(() => {
      const results = allTransactions.filter((tx) => {
        const txDate = tx.timestamp?.split("T")[0];
        const isAdminEdited = tx.editingBranch === "ADMIN_SYSTEM";
        const isInRange = txDate >= startDate && txDate <= endDate;

        return isAdminEdited && isInRange;
      });

      setFilteredResults(results);
      setIsSearching(false);
    }, 300);
  }, [allTransactions, startDate, endDate]);

  // 4. Excel Download (Uses current filtered results)
  const downloadExcel = () => {
    if (filteredResults.length === 0)
      return alert("No data to download. Please search first.");

    const headers = [
      "Date",
      "Payment ID",
      "Type",
      "From",
      "To",
      "Amount",
      "Remarks",
    ];
    const rows = filteredResults.map((tx) => [
      tx.timestamp.split("T")[0],
      tx.paymentId,
      tx.paymentType,
      tx.fromBranch,
      tx.toBranch,
      tx.amount,
      tx.remarks.replace(/,/g, ";"),
    ]);

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Admin_Audit_Edited_${startDate}_to_${endDate}.csv`);
    link.click();
  };

  return (
    <div
      className="admin-audit-container"
      style={{
        marginTop: "40px",
        borderTop: "3px solid #008069",
        paddingTop: "20px",
      }}
    >
      <div
        className="audit-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h3 style={{ margin: 0, color: "#008069" }}>
          <FaFilter /> Admin System Edit Log
        </h3>
        <button
          onClick={downloadExcel}
          className="download-excel-btn"
          style={{
            background: "#1d6f42",
            color: "white",
            border: "none",
            padding: "10px 15px",
            borderRadius: "5px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <FaFileExcel /> Export to Excel
        </button>
      </div>

      <div
        className="audit-filters"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "15px",
          marginBottom: "25px",
          background: "#f0f2f5",
          padding: "20px",
          borderRadius: "10px",
          alignItems: "flex-end",
        }}
      >
        <div className="filter-item">
          <label
            style={{
              display: "block",
              fontSize: "12px",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            START DATE
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <div className="filter-item">
          <label
            style={{
              display: "block",
              fontSize: "12px",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            END DATE
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        {/* THE SEPARATE SEARCH BUTTON */}
        <button
          onClick={executeSearch}
          disabled={isSearching}
          style={{
            background: "#008069",
            color: "white",
            border: "none",
            padding: "10px 25px",
            borderRadius: "5px",
            cursor: "pointer",
            height: "40px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "bold",
          }}
        >
          {isSearching ? <FaSync className="spin" /> : <FaSearch />} Search
          Records
        </button>
      </div>

      <div
        className="table-Container"
        style={{
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        }}
      >
        <table
          className="admin-table"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead style={{ background: "#e9edef" }}>
            <tr>
              <th style={{ padding: "12px" }}>Date</th>
              <th>Trans ID</th>
              <th>Type</th>
              <th>From → To</th>
              <th>Amount</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.length > 0 ? (
              filteredResults.map((tx) => (
                <tr
                  key={tx.paymentId}
                  style={{ borderBottom: "1px solid #eee" }}
                >
                  <td style={{ padding: "12px" }}>
                    {tx.timestamp.split("T")[0]}
                  </td>
                  <td style={{ fontSize: "11px", color: "#555" }}>
                    {tx.paymentId}
                  </td>
                  <td>
                    <span className={`pill ${tx.paymentType}`}>
                      {tx.paymentType}
                    </span>
                  </td>
                  <td>
                    {tx.fromBranch} → {tx.toBranch}
                  </td>
                  <td style={{ fontWeight: "bold" }}>
                    ₹{Number(tx.amount).toLocaleString()}
                  </td>
                  <td
                    style={{
                      fontStyle: "italic",
                      fontSize: "13px",
                      color: "#666",
                    }}
                  >
                    {tx.remarks}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#888",
                  }}
                >
                  {isSearching
                    ? "Searching database..."
                    : "No records found. Adjust filters and click Search."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
